import { useCallback, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Cancel from '@mui/icons-material/Cancel';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { FileCopy, SquareFoot } from '@mui/icons-material';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import { Can } from '@/components/Can';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { AnalyseViewpointButton } from './AnalyseViewpointButton/AnalyseViewpointButton';
import FilterIcon from '@mui/icons-material/Filter';
import * as state from '@/components/Analysis/state';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { useDrawerOpen } from '@/hooks/useDrawerOpen';
import { AnalysisDrawerRight, Permissions } from '@/types';
import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { ResponsiveButton, ResponsiveText, Toolbar } from './styles';
import { toggleValue } from '@/utils';
import { useFindViewpointsTool } from '@/components/Analysis/modules/findViewpointsTool';
import { FindViewpointsToolMenu } from '@/components/Analysis/modules/findViewpointsTool/FindViewpointsToolMenu';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useWindowSize } from '@/hooks/useWindowSize';
import { FabDropdown } from '@/components/shared/FabDropdown/FabDropdown';
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus';
import { useFocusOnSelectedAssembly } from '../../../hooks/useFocusOnSelectedAssembly';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { CurrentImageProps } from '@abyss/3d-viewer';
import { ScanNameDisplay } from './ScanNameDisplay';

type MenuBarProps = {
  updateCameraTarget: () => void;
};

type SphericalWithPaintRegion = CurrentImageProps & {
  paintRegion?: string;
};

export const MenuBar = ({ updateCameraTarget }: MenuBarProps) => {
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const { isMeasurementMenuVisible } = useMeasurementTool();
  const { isFindViewpointsToolActive, activateFindViewpointsTool } = useFindViewpointsTool();
  const [showPointCloudInSpherical, setShowPointCloudInSpherical] = useAtom(
    state.showPointCloudInSpherical
  );
  const inspectionMetadata = useAtomValue(state.inspectionMetadata);

  const [selectedSpherical, setSelectedSpherical] = useAtom<SphericalWithPaintRegion | undefined>(
    state.selectedSpherical
  );

  const structureLocationsMap = useAtomValue(state.structureLocationsMap);
  const setImageLayer = useSetAtom(state.imageLayer);

  const cancelPOIAdd = useSetAtom(poiState.cancelPOIAdd);
  const [drawerRight, setDrawerRight] = useAtom(state.drawerRight);

  const isBetaUser = useFeatureFlag('beta-user');
  const userCanUpdateAsset = useHavePermission(Permissions.UPDATE_ASSET);

  const isRightDrawerOpen = useDrawerOpen('right');
  const isLeftDrawerOpen = useDrawerOpen('left');
  const screenSize = useWindowSize();
  const { showSnackBar } = useSnackBarMessage({ variant: 'standard' });

  const { leftDrawerWidth, rightDrawerWidth } = useDrawerWidths();

  const exitSphericalButtonDistanceFromRight = useMemo(() => {
    return isRightDrawerOpen ? rightDrawerWidth + 70 : 70;
  }, [rightDrawerWidth, isRightDrawerOpen]);

  const buttonsDistanceFromLeft = useMemo(() => {
    return isLeftDrawerOpen ? leftDrawerWidth + 64 : 72;
  }, [leftDrawerWidth, isLeftDrawerOpen]);

  const closeImageOffset = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 32;
    }
    return 48;
  }, [screenSize]);

  const sphericalInfoOffset = useMemo(() => {
    const { width } = screenSize;

    if (width && width <= 1728) {
      return 190;
    }
    return 232;
  }, [screenSize]);

  const exitImageViewpoint = () => {
    setSelectedSpherical(undefined);
    cancelPOIAdd();
  };

  const toggleHighlightAsset = () => {
    setShowPointCloudInSpherical((current) => !current);
  };

  const handleGenerateDeepLinkClicked = useCallback(() => {
    updateCameraTarget();
    navigator.clipboard.writeText(window.location.href);
    showSnackBar('Url copied to clipboard', 'success');
  }, [showSnackBar, updateCameraTarget]);

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
    },
    [drawerRight, setDrawerRight]
  );

  const { measurementToolHandlerForAdormants, cancelMeasuring } = useMeasurementTool();
  const enableMeasureTool = useCallback(() => {
    measurementToolHandlerForAdormants();
    handleRightToggle('measuringTool');
  }, [handleRightToggle, measurementToolHandlerForAdormants]);

  useEffect(() => {
    if (!drawerRight.measuringTool) {
      cancelMeasuring();
    }
  }, [cancelMeasuring, drawerRight]);

  const { focusOnAssembly } = useFocusOnSelectedAssembly(selectedSpherical);

  const enableFindViewpointsTool = useCallback(() => {
    activateFindViewpointsTool();
    handleRightToggle('findViewpointsTool');
  }, [handleRightToggle, activateFindViewpointsTool]);

  const hasActiveTool = isMeasurementMenuVisible || isFindViewpointsToolActive;

  const imageLayers = useMemo(() => {
    if (selectedSpherical?.id && structureLocationsMap) {
      return structureLocationsMap?.get(selectedSpherical.id)?.layers ?? [];
    }
    return [];
  }, [selectedSpherical?.id, structureLocationsMap]);

  const imageLayersOptions = useMemo(() => {
    const options = imageLayers?.map((layer) => layer?.displayName ?? '') ?? [];
    const defaultText = 'Default';
    if (!options.includes(defaultText)) {
      options.unshift(defaultText);
    }
    return options ?? [];
  }, [imageLayers]);

  const changeImage = useCallback(
    (selectedIndex: number) => {
      if (imageLayers) {
        const imageLayer = imageLayers[selectedIndex - 1]; // Cater for Default being index 0
        setImageLayer(imageLayer ?? undefined);
      }

      setSelectedImage(selectedIndex);
      setSelectedSpherical((current) => {
        if (!current) return current;
        const currentSpherical = { ...current };
        return currentSpherical;
      });
    },
    [imageLayers, setImageLayer, setSelectedSpherical]
  );

  return (
    <>
      {isFindViewpointsToolActive && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: buttonsDistanceFromLeft,
          }}
        >
          <FindViewpointsToolMenu />
        </div>
      )}
      <Toolbar offset={buttonsDistanceFromLeft}>
        <Stack gap={1} mb={1} direction="row">
          {!hasActiveTool && (
            <ResponsiveButton
              variant="contained"
              className="responsive-btn"
              onClick={handleGenerateDeepLinkClicked}
              startIcon={<FileCopy />}
            >
              Copy view
            </ResponsiveButton>
          )}
          {!hasActiveTool && (
            <Can I="read" a="PointOfInterest">
              <ResponsiveButton
                variant="contained"
                className="responsive-btn"
                onClick={enableMeasureTool}
                startIcon={<SquareFoot />}
              >
                Measure
              </ResponsiveButton>
            </Can>
          )}
          {!hasActiveTool && !selectedSpherical && inspectionMetadata?.voxelLocationsPath && (
            <Can I="read" a="PointOfInterest">
              <ResponsiveButton
                variant="contained"
                className="responsive-btn"
                onClick={enableFindViewpointsTool}
                startIcon={<AdsClickIcon />}
              >
                Find viewpoint
              </ResponsiveButton>
            </Can>
          )}
          {isBetaUser && !hasActiveTool && imageLayers?.length !== 0 && !selectedSpherical && (
            <FabDropdown
              icon={<FilterIcon sx={{ mr: 1 }} />}
              title="Toggle Image"
              options={imageLayersOptions}
              onSelect={(index) => changeImage(index)}
              selectedIndex={selectedImage}
            />
          )}
        </Stack>
        <Stack gap={1} direction="row" zIndex={102}>
          {!hasActiveTool && selectedSpherical && inspectionMetadata?.voxelLocationsPath && (
            <Can I="read" a="PointOfInterest">
              <ResponsiveButton
                variant="contained"
                className="responsive-btn"
                onClick={enableFindViewpointsTool}
                startIcon={<AdsClickIcon />}
              >
                Find viewpoint
              </ResponsiveButton>
            </Can>
          )}
          {selectedSpherical && (
            <ResponsiveButton
              variant="contained"
              onClick={focusOnAssembly}
              startIcon={<FilterCenterFocusIcon />}
            >
              Focus on Component
            </ResponsiveButton>
          )}
          {isBetaUser && !hasActiveTool && imageLayers?.length !== 0 && selectedSpherical && (
            <FabDropdown
              icon={<FilterIcon sx={{ mr: 1 }} />}
              title="Toggle Image"
              options={imageLayersOptions}
              onSelect={(index) => changeImage(index)}
              selectedIndex={selectedImage}
            />
          )}
          {selectedSpherical && userCanUpdateAsset && (
            <>
              <ResponsiveButton variant="contained" onClick={toggleHighlightAsset}>
                {showPointCloudInSpherical ? 'Remove highlight' : 'Highlight asset'}
              </ResponsiveButton>
              <AnalyseViewpointButton />
            </>
          )}
        </Stack>
      </Toolbar>

      {selectedSpherical && (
        <>
          <ResponsiveText
            sx={{
              position: 'absolute',
              right: exitSphericalButtonDistanceFromRight + sphericalInfoOffset,
              color: 'white',
              top: 10,
              zIndex: 102,
            }}
          >
            <ScanNameDisplay selectedLocation={selectedSpherical} />
          </ResponsiveText>
          <ResponsiveButton
            variant="contained"
            className="responsive-btn"
            sx={{
              position: 'absolute',
              right: exitSphericalButtonDistanceFromRight + closeImageOffset,
              top: 10,
              zIndex: 102,
            }}
            onClick={exitImageViewpoint}
            startIcon={<Cancel />}
          >
            Close image view
          </ResponsiveButton>
        </>
      )}
    </>
  );
};
